import { useMemo } from "react";
import { debugLog } from "../lib/debug";
import type { PreChatSuggestions } from "../lib/resolve-suggestions";
import { toSuggestions } from "../lib/resolve-suggestions";
import type {
	EmbedConfig,
	PagePrompt,
	PagePromptTier,
	PageSuggestionsEntry,
} from "./config";
import { usePathname } from "./use-pathname";

/** How many of a page's prompts the widget shows at a time. */
const SHOWN_SUGGESTION_COUNT = 3;

/** Tier display order; one prompt is shown per tier. */
const TIER_ORDER: PagePromptTier[] = ["low", "medium", "high"];

function takeRandom<T>(pool: T[]): T | undefined {
	if (pool.length === 0) {
		return undefined;
	}
	const index = Math.floor(Math.random() * pool.length);
	const [taken] = pool.splice(index, 1);
	return taken;
}

/** Up to {@link SHOWN_SUGGESTION_COUNT} prompts, one per tier, random within pools;
 *  untagged prompts fill any slot and leftovers top up short slots. */
export function pickPagePrompts(prompts: PagePrompt[]): PagePrompt[] {
	const byTier = new Map<PagePromptTier, PagePrompt[]>();
	const wildcards: PagePrompt[] = [];
	for (const prompt of prompts) {
		if (prompt.tier) {
			const pool = byTier.get(prompt.tier) ?? [];
			pool.push(prompt);
			byTier.set(prompt.tier, pool);
		} else {
			wildcards.push(prompt);
		}
	}

	const picked: PagePrompt[] = [];
	for (const tier of TIER_ORDER) {
		const choice = takeRandom(byTier.get(tier) ?? []) ?? takeRandom(wildcards);
		if (choice) {
			picked.push(choice);
		}
	}

	const leftovers = [...byTier.values()].flat().concat(wildcards);
	while (picked.length < Math.min(SHOWN_SUGGESTION_COUNT, prompts.length)) {
		const filler = takeRandom(leftovers);
		if (!filler) {
			break;
		}
		picked.push(filler);
	}
	return picked;
}

/** URL → entry key: pathname only, lowercased, no query/hash/trailing slash. */
export function normalizePathname(url: string): string {
	let pathname: string;
	try {
		pathname = new URL(url, "http://localhost").pathname;
	} catch {
		pathname = "/";
	}
	const trimmed = pathname.replace(/\/+$/, "");
	return (trimmed === "" ? "/" : trimmed).toLowerCase();
}

function fragmentOf(url: string): string {
	try {
		return decodeURIComponent(new URL(url, "http://localhost").hash.slice(1));
	} catch {
		return "";
	}
}

/**
 * The entry for this page: one whose `#fragment` names the section the
 * visitor paused in, else the page's fragment-free entry, else its first.
 */
export function pickSectionSuggestions(
	entries: PageSuggestionsEntry[],
	pathname: string,
	sectionKey: string | null,
): PageSuggestionsEntry | undefined {
	const current = normalizePathname(pathname);
	const onPage = entries.filter(
		(entry) => normalizePathname(entry.url) === current,
	);
	const section =
		sectionKey === null
			? undefined
			: onPage.find((entry) => fragmentOf(entry.url) === sectionKey);
	return (
		section ?? onPage.find((entry) => fragmentOf(entry.url) === "") ?? onPage[0]
	);
}

/** Validate a raw `pageSuggestions` value; malformed entries drop out. */
export function parsePageSuggestions(value: unknown): PageSuggestionsEntry[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.flatMap((entry): PageSuggestionsEntry[] => {
		const item = entry as {
			url?: unknown;
			prompts?: unknown;
		} | null;
		if (
			typeof item?.url !== "string" ||
			item.url.length === 0 ||
			!Array.isArray(item.prompts)
		) {
			return [];
		}
		const prompts = item.prompts.flatMap((raw): PagePrompt[] => {
			const prompt = raw as Partial<PagePrompt> | null;
			if (typeof prompt?.text !== "string" || prompt.text.length === 0) {
				return [];
			}
			return [
				{
					id: typeof prompt.id === "string" ? prompt.id : null,
					text: prompt.text,
					tier:
						prompt.tier === "low" ||
						prompt.tier === "medium" ||
						prompt.tier === "high"
							? prompt.tier
							: undefined,
				},
			];
		});
		return prompts.length > 0 ? [{ url: item.url, prompts }] : [];
	});
}

/**
 * The dashboard-configured rungs for the current page: the matched entry's
 * picked prompts (`page`) and the channel's fixed list (`channel`), kept
 * separate so the pill row can attribute its origin exactly. Memoized because
 * the pick is random and the identity has to hold: `useSuggestions` keys an
 * effect on this object. `sectionKey` is the section the scroll trigger
 * last paused in, which can swap the page entry for a `#fragment` one.
 */
export function useSuggestions(
	config: EmbedConfig,
	sectionKey: string | null = null,
): PreChatSuggestions {
	const { pageSuggestions, suggestions } = config;
	const pathname = usePathname();

	const entry = useMemo(
		() => pickSectionSuggestions(pageSuggestions ?? [], pathname, sectionKey),
		[pathname, pageSuggestions, sectionKey],
	);

	return useMemo(() => {
		const page = entry
			? pickPagePrompts(entry.prompts).map(({ id, text }) => ({ id, text }))
			: null;
		debugLog("configured suggestions resolve", {
			pathname,
			entry: entry?.url ?? null,
			count: page?.length ?? 0,
		});
		return { page, channel: toSuggestions(suggestions ?? []) };
	}, [pathname, entry, suggestions]);
}
